// =====================================================================================================================
// ========== IMPORTS
// =====================================================================================================================

import { useState, useEffect, useMemo, useCallback } from "react"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Checkbox from "@mui/material/Checkbox"
import CircularProgress from "@mui/material/CircularProgress"
import DeleteIcon from "@mui/icons-material/Delete"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import Grid from "@mui/material/Grid2"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Select from "@mui/material/Select"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import { darken, lighten, styled } from "@mui/system"
import dayjs from "dayjs"

// =====================================================================================================================
// ========== CONSTANTS
//
// Rationale: price_index_id and index_name are kept as module-level constants (the original TODO comments
// acknowledged they should eventually be made configurable; when that time comes they move to a settings fetch).
// =====================================================================================================================

const PRICE_INDEX_ID = "eaad0a50-bd1c-43fa-bf8e-f61e0da9933c"
const INDEX_NAME     = "IRL"
const TODAY          = new Date().toISOString().split("T")[0]

const PAYMENT_FREQUENCIES = {
    "1 month": "Mensuel",
    "3 month": "Trimestriel",
    "1 year":  "Annuel",
}

const DEFAULT_TENANT = { id: "", kind: "person", label: "", value: "undefined/undefined" }
const DEFAULT_LEASE  = {
    property_id:       "",
    start_date:        TODAY,
    payment_frequency: "",
    furnished:         false,
}

// =====================================================================================================================
// ========== GUI COMPONENTS
// =====================================================================================================================

const FormGrid = styled(Grid)(() => ({
    display:       "flex",
    flexDirection: "column",
}))

const GroupHeader = styled("div")(({ theme }) => ({
    position:        "sticky",
    top:             "-8px",
    padding:         "4px 10px",
    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
    ...theme.applyStyles("dark", {
        backgroundColor: darken(theme.palette.secondary.main, 0.8),
    }),
}))

const GroupItems = styled("ul")({ padding: 0 })

// =====================================================================================================================
// ========== SKELETON
//
// Rationale: the skeleton mirrors the real form structure field-by-field so the user perceives
// zero layout shift when real content arrives.
// =====================================================================================================================

function FormSkeleton() {
    const field = (xs) => (
        <FormGrid size={{ xs }}>
            <Skeleton width="45%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        </FormGrid>
    )
    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>
                    {field(12)}
                    {field(6)}
                    {field(6)}
                    {field(6)}
                    {field(6)}
                    {field(12)}
                    {field(12)}
                    <FormGrid size={{ xs: 12 }} sx={{ mt: 1 }}>
                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                </Grid>
            </CardContent>
        </Card>
    )
}

// =====================================================================================================================
// ========== HELPERS
// =====================================================================================================================

/** Parse a Supabase error into a human-readable French message. */
function parseError(err) {
    if (!err) return null
    // Foreign-key violation (PostgreSQL error code 23503)
    if (err.code === "23503") {
        return "Impossible de supprimer ce bail : des données liées existent encore (transactions, etc.)."
    }
    // Unique constraint (23505)
    if (err.code === "23505") {
        return "Un bail identique existe déjà."
    }
    return err.message ?? "Une erreur inattendue s'est produite."
}

/** Build tenant option objects from raw Supabase company/person rows. */
function buildTenantOptions(companies, persons) {
    const companyOpts = (companies ?? [])
        .map((c) => ({ id: c.id, kind: "company", label: c.name, value: `${c.id}/undefined` }))
        .sort((a, b) => a.label.localeCompare(b.label))

    const personOpts = (persons ?? [])
        .map((p) => ({
            id:    p.id,
            kind:  "person",
            label: `${p.first_name} ${p.last_name}`,
            value: `undefined/${p.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return [...companyOpts, ...personOpts]
}

// =====================================================================================================================
// ========== LEASES FORM
//
// Data flow rationale:
//  1. Static options (properties, tenantOptions, categories) are fetched once on mount via Promise.all
//     to minimise total round-trips.
//  2. Lease-specific data (lease row, buckets, tenants) is fetched only when selectedLeaseId changes
//     AND after options have loaded (isLoadingOptions === false guard).  This prevents a race where
//     defaultLeaseBuckets is still empty when we try to populate the form.
//  3. Mutations call Supabase directly; any error is caught and shown in an Alert component inside the
//     card so the user never has to open dev-tools to understand what went wrong.
// =====================================================================================================================

export default function LeasesForm({ supabase, selectedLeaseId, onSuccess }) {

    // ── STATIC OPTIONS ───────────────────────────────────────────────────────────────────────────────────

    const [properties,    setProperties]    = useState([])
    const [tenantOptions, setTenantOptions] = useState([])
    const [categories,    setCategories]    = useState([])
    const [isLoadingOptions, setIsLoadingOptions] = useState(true)
    const [optionsError,     setOptionsError]     = useState(null)

    useEffect(() => {
        let cancelled = false
        const fetchOptions = async () => {
            setIsLoadingOptions(true)
            setOptionsError(null)
            try {
                const [propertiesRes, companiesRes, personsRes, categoryRes] = await Promise.all([
                    supabase.from("property").select("id, short_name").order("short_name"),
                    supabase.from("company").select("id, name"),
                    supabase.from("person").select("id, first_name, last_name"),
                    supabase
                        .from("setting")
                        .select("value")
                        .eq("label", "transactions_buckets_categories")
                        .order("row_owner_id", { nullsFirst: false }) // user-specific rows before global defaults
                        .limit(1)
                        .maybeSingle(),
                ])

                if (propertiesRes.error) throw propertiesRes.error
                if (companiesRes.error)  throw companiesRes.error
                if (personsRes.error)    throw personsRes.error
                // categoryRes error is non-fatal: if no setting exists we default to []

                if (!cancelled) {
                    setProperties(propertiesRes.data ?? [])
                    setTenantOptions(buildTenantOptions(companiesRes.data, personsRes.data))
                    setCategories(categoryRes.data?.value?.split("|") ?? [])
                }
            } catch (err) {
                if (!cancelled) setOptionsError(parseError(err))
            } finally {
                if (!cancelled) setIsLoadingOptions(false)
            }
        }
        fetchOptions()
        return () => { cancelled = true }
    }, [supabase])

    // ── DEFAULT BUCKETS (depends on categories) ──────────────────────────────────────────────────────────

    const defaultLeaseBuckets = useMemo(
        () => categories.map((category) => ({ category, default_value: "" })),
        [categories]
    )

    // ── FORM STATE ───────────────────────────────────────────────────────────────────────────────────────

    const [lease,   setLease]   = useState(DEFAULT_LEASE)
    const [buckets, setBuckets] = useState([])
    const [tenants, setTenants] = useState([DEFAULT_TENANT])

    const [isLoadingLease, setIsLoadingLease] = useState(false)
    const [isSubmitting,   setIsSubmitting]   = useState(false)
    const [mutationError,  setMutationError]  = useState(null)

    // ── LEASE-SPECIFIC FETCH ─────────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        // Wait for static options so defaultLeaseBuckets is already populated.
        if (isLoadingOptions) return

        if (!selectedLeaseId) {
            // Clearing selection → reset to blank form
            setLease(DEFAULT_LEASE)
            setBuckets(defaultLeaseBuckets)
            setTenants([DEFAULT_TENANT])
            setMutationError(null)
            return
        }

        let cancelled = false
        const fetchLeaseData = async () => {
            setIsLoadingLease(true)
            setMutationError(null)
            try {
                const [leaseRes, bucketsRes, tenantsRes] = await Promise.all([
                    supabase.from("lease").select("*").eq("id", selectedLeaseId).single(),
                    supabase
                        .from("transactions_bucket")
                        .select("*")
                        .eq("lease_id", selectedLeaseId)
                        .or(`valid_until.is.null,valid_until.gte.${TODAY}`)
                        .order("id"),
                    supabase
                        .from("tenant")
                        .select("*, company:company_id(id, name), person:person_id(id, first_name, last_name)")
                        .eq("lease_id", selectedLeaseId)
                        .order("id"),
                ])

                if (leaseRes.error)   throw leaseRes.error
                if (bucketsRes.error) throw bucketsRes.error
                if (tenantsRes.error) throw tenantsRes.error

                if (!cancelled) {
                    setLease(leaseRes.data)

                    setBuckets(
                        bucketsRes.data?.length > 0
                            ? bucketsRes.data
                            : defaultLeaseBuckets
                    )

                    const formattedTenants = (tenantsRes.data ?? []).map((t) => ({
                        id:    t.id,
                        kind:  t.company_id ? "company" : "person",
                        label: t.company
                            ? t.company.name
                            : `${t.person?.first_name ?? ""} ${t.person?.last_name ?? ""}`.trim(),
                        value: t.company_id
                            ? `${t.company_id}/undefined`
                            : `undefined/${t.person_id}`,
                    }))

                    setTenants(formattedTenants.length > 0 ? formattedTenants : [DEFAULT_TENANT])
                }
            } catch (err) {
                if (!cancelled) setMutationError(parseError(err))
            } finally {
                if (!cancelled) setIsLoadingLease(false)
            }
        }

        fetchLeaseData()
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLeaseId, isLoadingOptions])
    // Intentionally omitting defaultLeaseBuckets from deps: it is stable once options load, and
    // including it would cause a double-fetch when categories arrive at the same time as selectedLeaseId.

    // ── FORM CHANGE HANDLERS ─────────────────────────────────────────────────────────────────────────────

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target
        setLease((prev) => ({ ...prev, [name]: value }))
    }, [])

    const handleInputChangeDate = useCallback((e) => {
        const { name, value } = e.target
        setLease((prev) => ({ ...prev, [name]: value })) // keep as ISO string; dayjs only for display
    }, [])

    const handleSwitchChange = useCallback((e) => {
        const { name, checked } = e.target
        setLease((prev) => ({ ...prev, [name]: checked }))
    }, [])

    const handlePropertyChange = useCallback((_e, value) => {
        setLease((prev) => ({ ...prev, property_id: value?.id ?? "" }))
    }, [])

    const handleBucketChange = useCallback((category, value) => {
        setBuckets((prev) =>
            prev.map((b) => (b.category === category ? { ...b, default_value: value } : b))
        )
    }, [])

    const addTenant = useCallback(() => {
        setTenants((prev) => [...prev, DEFAULT_TENANT])
    }, [])

    const removeTenant = useCallback((index) => {
        setTenants((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const handleTenantChange = useCallback((index, value) => {
        setTenants((prev) => prev.map((t, i) => (i === index ? value : t)))
    }, [])

    // ── RESET HELPER ─────────────────────────────────────────────────────────────────────────────────────

    const resetForm = useCallback(() => {
        setLease(DEFAULT_LEASE)
        setBuckets(defaultLeaseBuckets)
        setTenants([DEFAULT_TENANT])
        setMutationError(null)
    }, [defaultLeaseBuckets])

    // ── SUBMIT (INSERT / UPDATE) ─────────────────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true)
        setMutationError(null)

        const leasePayload = { ...lease, reference_price_index_name: INDEX_NAME }

        try {
            if (selectedLeaseId) {
                // ── UPDATE ──
                const { id: _id, created_at: _c, updated_at: _u, ...updatePayload } = leasePayload
                const { error: updateError } = await supabase
                    .from("lease")
                    .update(updatePayload)
                    .eq("id", selectedLeaseId)
                if (updateError) throw updateError

                // Replace buckets: delete then re-insert (preserves original behaviour)
                const { error: delBucketsError } = await supabase
                    .from("transactions_bucket")
                    .delete()
                    .eq("lease_id", selectedLeaseId)
                if (delBucketsError) throw delBucketsError

                // Replace tenants: delete then re-insert
                const { error: delTenantsError } = await supabase
                    .from("tenant")
                    .delete()
                    .eq("lease_id", selectedLeaseId)
                if (delTenantsError) throw delTenantsError

                await insertBucketsAndTenants(selectedLeaseId, buckets, tenants)

            } else {
                // ── INSERT ──
                const { data: inserted, error: insertError } = await supabase
                    .from("lease")
                    .insert(leasePayload)
                    .select("id")
                    .single()
                if (insertError) throw insertError

                await insertBucketsAndTenants(inserted.id, buckets, tenants)
                onSuccess?.() // clear selectedLeaseId in parent
            }

            resetForm()
        } catch (err) {
            setMutationError(parseError(err))
        } finally {
            setIsSubmitting(false)
        }
    }, [lease, buckets, tenants, selectedLeaseId, onSuccess, resetForm, supabase])

    // ── DELETE ───────────────────────────────────────────────────────────────────────────────────────────

    const handleDelete = useCallback(async () => {
        if (!selectedLeaseId) return
        setIsSubmitting(true)
        setMutationError(null)
        try {
            // Delete dependents first to avoid FK errors on the lease row itself.
            const { error: delBucketsError } = await supabase
                .from("transactions_bucket")
                .delete()
                .eq("lease_id", selectedLeaseId)
            if (delBucketsError) throw delBucketsError

            const { error: delTenantsError } = await supabase
                .from("tenant")
                .delete()
                .eq("lease_id", selectedLeaseId)
            if (delTenantsError) throw delTenantsError

            const { error: delLeaseError } = await supabase
                .from("lease")
                .delete()
                .eq("id", selectedLeaseId)
            if (delLeaseError) throw delLeaseError

            resetForm()
            onSuccess?.()
        } catch (err) {
            // Surfaces FK violations clearly (e.g. if a transaction still references this lease)
            setMutationError(parseError(err))
        } finally {
            setIsSubmitting(false)
        }
    }, [selectedLeaseId, onSuccess, resetForm, supabase])

    // ── INSERT HELPER (shared by insert + update paths) ──────────────────────────────────────────────────

    const insertBucketsAndTenants = useCallback(async (leaseId, bkts, tnts) => {
        if (bkts.length > 0) {
            const { error } = await supabase.from("transactions_bucket").insert(
                bkts.map((b) => ({
                    category:      b.category,
                    default_value: parseFloat(b.default_value || 0),
                    lease_id:      leaseId,
                    price_index_id: PRICE_INDEX_ID,
                }))
            )
            if (error) throw error
        }

        if (tnts.length > 0) {
            const tenantRows = tnts
                .map((t) => {
                    const personId  = t.value?.split("/")?.[1]
                    const companyId = t.value?.split("/")?.[0]
                    return Object.fromEntries(
                        Object.entries({
                            lease_id:   leaseId,
                            person_id:  personId  === "undefined" ? undefined : personId,
                            company_id: companyId === "undefined" ? undefined : companyId,
                        }).filter(([, v]) => v !== undefined && v !== null)
                    )
                })
                .filter((r) => r.person_id || r.company_id) // skip blank rows

            if (tenantRows.length > 0) {
                const { error } = await supabase.from("tenant").insert(tenantRows)
                if (error) throw error
            }
        }
    }, [supabase])

    // ── DERIVED VALUES FOR CONTROLLED INPUTS ─────────────────────────────────────────────────────────────

    const selectedProperty = useMemo(
        () => properties.find((p) => p.id === lease.property_id) ?? null,
        [properties, lease.property_id]
    )

    // ── RENDER: loading states ────────────────────────────────────────────────────────────────────────────

    if (isLoadingOptions) return <FormSkeleton />
    if (optionsError)     return <Alert severity="error">{optionsError}</Alert>
    if (isLoadingLease)   return <FormSkeleton />

    // ── RENDER: form ─────────────────────────────────────────────────────────────────────────────────────

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>

                    {/* ── MUTATION ERROR BANNER ── */}
                    {mutationError && (
                        <Grid size={{ xs: 12 }}>
                            <Alert
                                severity="error"
                                onClose={() => setMutationError(null)}
                            >
                                {mutationError}
                            </Alert>
                        </Grid>
                    )}

                    {/* ── PROPRIÉTÉ (Autocomplete — consistent with Locataires UX) ──
                        Rationale: the original Select required knowing the UUID to pre-select;
                        Autocomplete gives the user search/filter and shows the label immediately. */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="property" required>
                            Propriété
                        </FormLabel>
                        <Autocomplete
                            id="property"
                            disablePortal
                            disableClearable={!!lease.property_id}
                            options={properties}
                            getOptionLabel={(opt) => opt?.short_name ?? ""}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            value={selectedProperty}
                            onChange={handlePropertyChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Sélectionnez une propriété"
                                    size="small"
                                    required
                                />
                            )}
                        />
                    </FormGrid>

                    {/* ── FRÉQUENCE DE PAIEMENT ── */}
                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="paymentFrequency" required>
                            Fréquence de paiement
                        </FormLabel>
                        <Select
                            id="paymentFrequency"
                            name="payment_frequency"
                            value={lease.payment_frequency ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        >
                            {Object.entries(PAYMENT_FREQUENCIES).map(([id, label]) => (
                                <MenuItem key={id} value={id}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormGrid>

                    {/* ── DATE DE DÉBUT ── */}
                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="startDate" required>
                            Date de début
                        </FormLabel>
                        <OutlinedInput
                            id="startDate"
                            name="start_date"
                            type="date"
                            value={dayjs(lease.start_date).format("YYYY-MM-DD")}
                            onChange={handleInputChangeDate}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── BUCKETS (dynamic payment categories) ── */}
                    {buckets.map((bucket, index) => (
                        <FormGrid key={bucket.category} size={{ xs: 6 }}>
                            <FormLabel htmlFor={`bucket-${index}`} required>
                                {bucket.category}
                            </FormLabel>
                            <OutlinedInput
                                id={`bucket-${index}`}
                                placeholder="123"
                                endAdornment="€"
                                value={bucket.default_value}
                                onChange={(e) => handleBucketChange(bucket.category, e.target.value)}
                                required
                                size="small"
                                type="number"
                            />
                        </FormGrid>
                    ))}

                    {/* ── MEUBLÉ ── */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="furnished"
                                    name="furnished"
                                    checked={!!lease.furnished}
                                    onChange={handleSwitchChange}
                                />
                            }
                            label="Location meublée"
                        />
                    </FormGrid>

                    {/* ── LOCATAIRES ── */}
                    <FormGrid container spacing={1} size={{ xs: 12 }}>
                        <FormLabel required>Locataires</FormLabel>
                        {tenants.map((tenant, index) => (
                            <Grid container key={index} spacing={1}>
                                <FormGrid size={{ xs: 10 }}>
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        groupBy={(opt) => opt?.kind}
                                        options={tenantOptions}
                                        getOptionLabel={(opt) => opt?.label ?? ""}
                                        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                                        value={tenant}
                                        onChange={(_e, v) => handleTenantChange(index, v)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="M. Chirac (Jacques)"
                                                size="small"
                                            />
                                        )}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <GroupHeader>
                                                    {params.group === "company" ? "Entreprises" : "Personnes"}
                                                </GroupHeader>
                                                <GroupItems>{params.children}</GroupItems>
                                            </li>
                                        )}
                                    />
                                </FormGrid>
                                <FormGrid size={{ xs: 2 }}>
                                    <IconButton
                                        color="error"
                                        onClick={() => removeTenant(index)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </FormGrid>
                            </Grid>
                        ))}
                        <Button variant="text" onClick={addTenant} size="small">
                            Ajouter un locataire
                        </Button>
                    </FormGrid>

                    {/* ── ACTIONS ── */}
                    <FormGrid size={{ xs: selectedLeaseId ? 6 : 12 }} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Valider
                        </Button>
                    </FormGrid>

                    {selectedLeaseId && (
                        <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={isSubmitting}
                            >
                                Supprimer
                            </Button>
                        </FormGrid>
                    )}

                </Grid>
            </CardContent>
        </Card>
    )
}